import { Tag as TagIcon } from "@phosphor-icons/react";
import { useCallback } from "react";

import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import TEMPLATE_VARIABLES from "common/constants/templateVariables";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { EditorVariable } from "common/types";

import type { Editor } from "@tiptap/react";

interface VariablesProps {
  editor: Editor;
  customVariables: { name: string; value: string }[];
}

export default function Variables({ editor, customVariables }: VariablesProps) {
  const isDesktop = useTwBreakpoint("lg");

  const addVariable = useCallback(
    (variable: EditorVariable) => {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "variable",
          attrs: variable,
        })
        .insertContent(" ") // add an extra space after the variable
        .run();
    },
    [editor],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        disabled={!editor.isEditable}
        data-cy="variables-dropdown-button"
      >
        <Button
          intent="iconOnly"
          variant="ghost-black"
          size={isDesktop ? "xs" : "md"}
          className="data-[state=open]:bg-black-100"
          aria-label="Add variable"
        >
          <TagIcon />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        className="z-aboveDialog"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuList>
          {customVariables.map(({ value, name }) => (
            <DropdownMenuItem
              key={value}
              onClick={() =>
                addVariable({
                  id: value,
                  label: name,
                  isCustomVar: true,
                  fallback: "",
                })
              }
              data-cy={`variable-${value}`}
              className="text-blue-500 focus:bg-blue-50"
            >
              {name}
            </DropdownMenuItem>
          ))}
          {TEMPLATE_VARIABLES.map(({ value, name }) => (
            <DropdownMenuItem
              key={value}
              onClick={() =>
                addVariable({
                  id: value,
                  label: name,
                  isCustomVar: false,
                  fallback: "",
                })
              }
              data-cy={`variable-${value}`}
            >
              {name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
